import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../types';
import { AdvancedFilterValue } from '../../models/advancedFilters';

export interface KeypointFilterState {
  view: AdvancedFilterValue | undefined;
  features: AdvancedFilterValue[];
  advancedFilters: {
    datasources: AdvancedFilterValue;
    dicomICID: string;
    study: string;
    annotationSetName?: string;
    segmentationICID: string;
    keypointICID: string;
    frameId?: string;
    reviewStatus?: string[];
    technician: string;
    created_at_after: string | undefined;
    created_at_before: string | undefined;
    updated_at_after: string | undefined;
    updated_at_before: string | undefined;
  };
  activeTags: AdvancedFilterValue[];
}

const initialState: KeypointFilterState = {
  view: undefined,
  features: [],
  advancedFilters: {
    datasources: { uuid: '', name: '' },
    dicomICID: '',
    study: '',
    segmentationICID: '',
    keypointICID: '',
    technician: '',
    created_at_after: undefined,
    created_at_before: undefined,
    updated_at_after: undefined,
    updated_at_before: undefined,
    frameId: '',
    reviewStatus: [],
  },
  activeTags: [],
};

export const keypointFilterStateSlice = createSlice({
  name: 'keypointFilterState',
  initialState,
  reducers: {
    setView: (state, action: PayloadAction<AdvancedFilterValue | undefined>) => {
      state.view = action.payload;
      if (state.view !== action.payload) {
        state.features = [];
      }
    },
    setFeatures: (state, action: PayloadAction<AdvancedFilterValue[]>) => {
      state.features = action.payload;
    },
    setAdvancedFilter: (
      state,
      action: PayloadAction<{
        key: string;
        value: AdvancedFilterValue | AdvancedFilterValue[] | string | string[] | undefined;
      }>
    ) => {
      const { key, value } = action.payload;
      state.advancedFilters = {
        ...state.advancedFilters,
        [key]: value,
      } as any;
    },
    resetFilters: state => {
      state.view = initialState.view;
      state.features = initialState.features;
      state.advancedFilters = initialState.advancedFilters;
      state.activeTags = initialState.activeTags;
    },
    updateActiveTags: (state, action: PayloadAction<AdvancedFilterValue[]>) => {
      state.activeTags = action.payload;
    },
    removeFilterTag: (state, action: PayloadAction<AdvancedFilterValue>) => {
      const tag = action.payload;

      state.features = state.features.filter(feature => feature.uuid !== tag.uuid);

      if (state.view?.uuid === tag.uuid) {
        state.view = undefined;
      }

      Object.keys(state.advancedFilters).forEach(key => {
        const filterKey = key as keyof typeof state.advancedFilters;
        const filterValue = state.advancedFilters[filterKey] as any;

        if (Array.isArray(filterValue)) {
          const isStringArray = filterValue.length === 0 || typeof filterValue[0] === 'string';
          if (isStringArray) {
            (state.advancedFilters as any)[filterKey] = filterValue.filter((item: any) => {
              const itemUuid = `${filterKey}_${item}`;
              return itemUuid !== tag.uuid && item !== tag.uuid;
            });
          } else {
            (state.advancedFilters as any)[filterKey] = filterValue.filter(
              (item: any) => item?.uuid !== tag.uuid
            );
          }
        } else if (typeof filterValue === 'object' && filterValue !== null) {
          if ((filterValue as any).uuid === tag.uuid) {
            (state.advancedFilters as any)[filterKey] = { uuid: '', name: '' };
          }
        } else if (typeof filterValue === 'string') {
          if (tag.name.includes(filterValue) && filterValue !== '') {
            (state.advancedFilters as any)[filterKey] = '';
          }
        }
      });

      state.activeTags = state.activeTags.filter(t => t.uuid !== tag.uuid);
    },
  },
});

export const {
  setView,
  setFeatures,
  setAdvancedFilter,
  resetFilters,
  updateActiveTags,
  removeFilterTag,
} = keypointFilterStateSlice.actions;

export const selectKeypointFilterState = (state: RootState) => (state as any).keypointFilterState;
export const selectKeypointActiveFilters = (state: RootState) =>
  (state as any).keypointFilterState.activeTags;

export default keypointFilterStateSlice.reducer;
